import { apiClient } from './client';

/**
 * Login as a teacher using email and password.
 * The backend's /auth/teacher-login endpoint only accepts an `email` field
 * (no phone support) - callers must resolve/validate the identifier as an
 * email address before calling this.
 * @param {{ email: string, password: string }} credentials
 * @returns {{ accessToken, refreshToken, expiresIn, user }}
 */
export async function teacherLogin({ email, password }) {
  const { data } = await apiClient.post('/auth/teacher-login', { email, password });
  return data;
}

/**
 * Invite a teacher to a course
 * @param {string} courseId
 * @param {{ email?: string, phone?: string }} dto
 * @returns {{ id, token, email, phone, status, expiresAt, invitationLink }}
 */
export async function inviteTeacher(courseId, dto) {
  const { data } = await apiClient.post(`/courses/${courseId}/teachers/invite`, dto);
  return data;
}

/**
 * Get pending teacher invitations for a course
 * @param {string} courseId
 * @returns Array of pending invitations
 */
export async function getPendingInvitations(courseId) {
  const { data } = await apiClient.get(`/courses/${courseId}/teachers/invitations`);
  return data;
}

/**
 * Accept a teacher invitation
 * @param {{ token: string, phone?: string, name?: string }} dto
 * @returns {{ accessToken, refreshToken, expiresIn, courseId, courseName, user, isNewUser }}
 */
export async function acceptInvitation(dto) {
  const { data } = await apiClient.post('/auth/teachers/accept-invitation', dto);
  return data;
}

/**
 * Assign students to a teacher in a course
 * @param {string} courseId
 * @param {string} teacherId
 * @param {{ studentIds: string[] }} dto
 * @returns {{ assigned, skipped, errors }}
 */
export async function assignStudentsToTeacher(courseId, teacherId, dto) {
  const { data } = await apiClient.post(
    `/courses/${courseId}/teachers/${teacherId}/assign-students`,
    dto
  );
  return data;
}

/**
 * Get students list for a teacher in a course (alternative API call)
 * @param {string} courseId
 * @returns Array of students assigned to teacher
 */
export async function getStudentsForTeacher(courseId) {
  const { data } = await apiClient.get(`/teacher/courses/${courseId}/students`);
  return data;
}

/**
 * Get teachers for a course with activity metrics
 * @param {string} courseId
 * @returns Array of teachers with activity data
 */
export async function getTeachersForCourse(courseId) {
  const { data } = await apiClient.get(`/courses/${courseId}/teachers`);
  return data;
}

/**
 * Get teacher activity and courses
 * @param {string} courseId
 * @returns Array of teacher activity data
 */
export async function getTeacherActivity(courseId) {
  const { data } = await apiClient.get(`/teacher/courses/${courseId}`);
  return data;
}

/**
 * Remove a teacher from a course
 * @param {string} courseId
 * @param {string} teacherId
 * @returns {{ success, assignmentsRemoved, message }}
 */
export async function removeTeacherFromCourse(courseId, teacherId) {
  const { data } = await apiClient.delete(
    `/courses/${courseId}/teachers/${teacherId}`
  );
  return data;
}
